<template>
  <div class="wrapper msc-about-page">
    <header class="msc-about-hero">
      <div
        class="msc-about-hero__media"
        aria-hidden="true"
        style="background-image: url('/img/back-to-school-start-MACVKugZ8WQ.jpg')"
      ></div>
      <div class="msc-about-hero__scrim" aria-hidden="true"></div>
      <div class="msc-about-hero__content">
        <p class="msc-about-hero__eyebrow">About</p>
        <h1 class="msc-about-hero__title">mySchool.cafe</h1>
        <p class="msc-about-hero__lead">
          The parent-friendly way to order school meals — built for families, designed for schools,
          powered by the iCare Solution Suite.
        </p>
      </div>
    </header>

    <main class="msc-about-body">
      <div class="container msc-about-body__container">
        <section class="msc-about-section">
          <h2 class="msc-about-section__title">Lunch-time smiles, without the scramble</h2>
          <p class="msc-about-section__text">
            mySchool.cafe gives parents a clear, familiar place to view menus, place meal orders, keep
            an eye on account balances, and manage payments — so cafeteria day feels less like a
            last-minute rush and more like a routine that just works.
          </p>
          <p class="msc-about-section__text">
            For schools and organizations, it is a ready-made parent portal that sits on top of the
            systems your cafeteria already relies on. Families get a modern experience; your team keeps
            the operational control they need behind the scenes.
          </p>
        </section>

        <section class="msc-about-section">
          <h2 class="msc-about-section__title">What families can do</h2>
          <ul class="msc-about-highlights">
            <li class="msc-about-highlight">
              <span class="msc-about-highlight__mark" aria-hidden="true">1</span>
              <div>
                <h3 class="msc-about-highlight__title">Order meals online</h3>
                <p class="msc-about-highlight__text">
                  Browse school menus on a calendar, choose items and extras, and place orders before
                  the morning cutoff — typically 7&nbsp;a.m. in your school’s time zone.
                </p>
              </div>
            </li>
            <li class="msc-about-highlight">
              <span class="msc-about-highlight__mark" aria-hidden="true">2</span>
              <div>
                <h3 class="msc-about-highlight__title">Support every student in the household</h3>
                <p class="msc-about-highlight__text">
                  Link multiple students to one parent account, switch between calendars, and see
                  upcoming meal costs for each child or the whole household.
                </p>
              </div>
            </li>
            <li class="msc-about-highlight">
              <span class="msc-about-highlight__mark" aria-hidden="true">3</span>
              <div>
                <h3 class="msc-about-highlight__title">Manage prepaid balances with confidence</h3>
                <p class="msc-about-highlight__text">
                  Add funds securely, set reload preferences, watch low-balance warnings, and review
                  transaction history with printable receipts when you need them.
                </p>
              </div>
            </li>
            <li class="msc-about-highlight">
              <span class="msc-about-highlight__mark" aria-hidden="true">4</span>
              <div>
                <h3 class="msc-about-highlight__title">Stay informed</h3>
                <p class="msc-about-highlight__text">
                  School notes and happenings, nutrient information, monthly calendar views, and help
                  resources — all in one place after a simple school-code registration.
                </p>
              </div>
            </li>
          </ul>
        </section>

        <section class="msc-about-section">
          <h2 class="msc-about-section__title">Tightly integrated with the iCare Solution Suite</h2>
          <p class="msc-about-section__text">
            mySchool.cafe is not a stand-alone side project bolted onto a cafeteria. It is the parent
            experience for schools that run on Newcare’s
            <strong>iCare Solution Suite</strong> — the same platform that powers school food-service
            operations, accounts, and day-to-day cafeteria workflows.
          </p>
          <p class="msc-about-section__text">
            Orders, balances, menus, and student links stay in sync with iCare. Parents see a clean
            web experience; your cafeteria team continues working in the tools they already use. That
            means fewer duplicate systems, fewer “is this order real?” moments, and a smoother path from
            parent click to lunch tray.
          </p>
        </section>

        <section class="msc-about-section">
          <h2 class="msc-about-section__title">Built for schools and organizations</h2>
          <p class="msc-about-section__text">
            Whether you serve one campus or a multi-site program, mySchool.cafe is tenant-aware: each
            school gets its own menus, calendars, time zone, and parent-facing details. Registration is
            gated with a school code, so only your families join your program.
          </p>
          <p class="msc-about-section__text">
            Looking to offer online meal ordering without reinventing your back office? If your
            organization is on iCare — or evaluating the suite — mySchool.cafe is the natural parent
            portal that completes the picture.
          </p>
        </section>

        <section class="msc-about-section">
          <h2 class="msc-about-section__title">Who we are</h2>
          <p class="msc-about-section__text">
            mySchool.cafe is designed and operated by
            <a
              class="msc-about-link"
              href="https://www.newcare.international"
              target="_blank"
              rel="noopener noreferrer"
            >Newcare International, LLC</a>
            — the team behind the iCare Solution Suite. We build practical tools for schools and the
            people who feed them, with a simple goal: make lunch-time easier for families and more
            reliable for operators.
          </p>
          <p class="msc-about-section__text">
            Learn more about how parents get started on our
            <router-link class="msc-about-link" to="/help">Help</router-link>
            page, or review our
            <router-link class="msc-about-link" to="/privacy">Privacy Policy</router-link>
            and
            <router-link class="msc-about-link" to="/license">Terms of Use</router-link>.
          </p>
        </section>
      </div>
    </main>

    <section class="msc-about-cta">
      <div class="msc-about-cta__inner">
        <h2 class="msc-about-cta__title">Bring mySchool.cafe to your school</h2>
        <p class="msc-about-cta__text">
          Curious how this would look for your cafeteria program? We would love to walk you through
          the parent experience, the iCare connection, and what rollout can look like for your
          organization.
        </p>
        <div class="msc-about-cta__actions">
          <a
            class="btn btn-primary btn-round"
            href="mailto:info@newcare.io?subject=mySchool.cafe Information Request"
          >
            Email us
          </a>
          <router-link class="btn btn-success btn-round" to="/signup">
            Register For Access
          </router-link>
          <router-link class="btn btn-neutral btn-round" to="/help">
            Parent help
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  export default {
    name: 'about-us',
    bodyClass: 'msc-about-page',
  };
</script>
